import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPrincipals } from '../redux/principalActions';
import { fetchPrograms, fetchProcessors, fetchProgramWithId } from '../redux/programActions';
import { fetchTenants } from '../redux/tenantActions';
import type { RootState, AppDispatch } from '../redux/store';
import type { Principal, Program, Tenant } from '../types/domain';
import {
  isWalletAddressCollection as programIsWalletCollection,
  resolveProgramForCollection,
} from '../utils/collectionMode';

interface ProgramContextValue {
  principals: Principal[];
  programs: Program[];
  tenants: Tenant[];
  selectedPrincipalId: number | null;
  selectedProgramId: number | null;
  selectedPrincipal?: Principal;
  selectedProgram?: Program;
  setSelectedPrincipalId: (id: number | null) => void;
  setSelectedProgramId: (id: number | null) => void;
  loading: boolean;
  loadingPrincipals: boolean;
  loadingPrograms: boolean;
  loadingTenants: boolean;
  /** Program uses BLOCKCHAIN processor — collect wallet addresses instead of UPI VPAs. */
  isWalletAddressCollection: boolean;
}

const ProgramContext = createContext<ProgramContextValue | null>(null);

const STORAGE_KEYS = { principal: 'vpa_agent_principalId', program: 'vpa_agent_programId' } as const;

interface ProgramProviderProps {
  children: ReactNode;
}

export function ProgramProvider({ children }: ProgramProviderProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { principal_list } = useSelector((s: RootState) => s.principal);
  const { program_list, processors, program: programDetail } = useSelector(
    (s: RootState) => s.program,
  );
  const { tenant } = useSelector((s: RootState) => s.tenant);

  const [selectedPrincipalId, setSelectedPrincipalIdState] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.principal);
      return saved ? Number(saved) : null;
    } catch {
      return null;
    }
  });
  const [selectedProgramId, setSelectedProgramIdState] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.program);
      return saved ? Number(saved) : null;
    } catch {
      return null;
    }
  });

  const setSelectedPrincipalId = (id: number | null) => {
    setSelectedPrincipalIdState(id);
    setSelectedProgramIdState(null);
    if (id != null) localStorage.setItem(STORAGE_KEYS.principal, String(id));
    else localStorage.removeItem(STORAGE_KEYS.principal);
    localStorage.removeItem(STORAGE_KEYS.program);
  };

  const setSelectedProgramId = (id: number | null) => {
    setSelectedProgramIdState(id);
    if (id != null) localStorage.setItem(STORAGE_KEYS.program, String(id));
    else localStorage.removeItem(STORAGE_KEYS.program);
  };

  useEffect(() => {
    dispatch(fetchPrincipals());
    dispatch(fetchProcessors());
  }, [dispatch]);

  useEffect(() => {
    if (selectedPrincipalId != null) return;
    const rows: Principal[] = principal_list?.principals?.rows ?? [];
    if (rows.length === 1) {
      setSelectedPrincipalId(rows[0].principalId);
    }
  }, [principal_list, selectedPrincipalId]);

  useEffect(() => {
    if (!principal_list?.principals?.rows?.length) return;
    const ids = principal_list.principals.rows.map((r: Principal) => r.principalId);
    if (selectedPrincipalId != null && !ids.includes(selectedPrincipalId)) {
      setSelectedPrincipalIdState(null);
      setSelectedProgramIdState(null);
      localStorage.removeItem(STORAGE_KEYS.principal);
      localStorage.removeItem(STORAGE_KEYS.program);
    }
  }, [principal_list, selectedPrincipalId]);

  useEffect(() => {
    if (selectedPrincipalId == null) return;
    dispatch(fetchPrograms(selectedPrincipalId));
  }, [dispatch, selectedPrincipalId]);

  useEffect(() => {
    if (selectedPrincipalId == null || selectedProgramId != null) return;
    const rows: Program[] = program_list?.rows ?? [];
    if (rows.length === 1) {
      setSelectedProgramId(rows[0].programId);
    }
  }, [program_list, selectedPrincipalId, selectedProgramId]);

  useEffect(() => {
    if (!program_list?.rows?.length) return;
    const ids = program_list.rows.map((r: Program) => r.programId);
    if (selectedProgramId != null && !ids.includes(selectedProgramId)) {
      setSelectedProgramIdState(null);
      localStorage.removeItem(STORAGE_KEYS.program);
    }
  }, [program_list, selectedProgramId]);

  useEffect(() => {
    if (selectedProgramId == null) return;
    dispatch(fetchTenants(selectedProgramId));
  }, [dispatch, selectedProgramId]);

  useEffect(() => {
    if (selectedProgramId == null) return;
    dispatch(fetchProgramWithId(selectedProgramId));
  }, [dispatch, selectedProgramId]);

  const principals: Principal[] = principal_list?.principals?.rows ?? [];
  const programs: Program[] = program_list?.rows ?? [];
  const tenants: Tenant[] = tenant?.tenants ?? [];
  const loadingPrincipals = useSelector((s: RootState) => s.principal.loading);
  const loadingPrograms = useSelector((s: RootState) => s.program.loading);
  const loadingTenants = useSelector((s: RootState) => s.tenant.loading);
  const loading = loadingPrincipals || loadingPrograms || loadingTenants;

  const selectedPrincipal = principals.find((p) => p.principalId === selectedPrincipalId);
  const selectedProgram = programs.find((p) => p.programId === selectedProgramId);

  const mergedProgram = resolveProgramForCollection(
    selectedProgramId,
    programDetail,
    programs,
  );
  const isWalletAddressCollection = programIsWalletCollection(mergedProgram, processors);

  const value: ProgramContextValue = {
    principals,
    programs,
    tenants,
    selectedPrincipalId,
    selectedProgramId,
    selectedPrincipal,
    selectedProgram,
    setSelectedPrincipalId,
    setSelectedProgramId,
    loading,
    loadingPrincipals,
    loadingPrograms,
    loadingTenants,
    isWalletAddressCollection,
  };

  return (
    <ProgramContext.Provider value={value}>
      {children}
    </ProgramContext.Provider>
  );
}

export function useProgramContext(): ProgramContextValue {
  const ctx = useContext(ProgramContext);
  if (!ctx) throw new Error('useProgramContext must be used within ProgramProvider');
  return ctx;
}

